import {
  onNgrxFormsControlId,
  validateSequential,
} from '@innogy/utils-deprecated';
import { createReducer, on } from '@ngrx/store';
import {
  createFormGroupState,
  markAsSubmitted,
  MarkAsSubmittedAction,
  markAsUnsubmitted,
  onNgrxForms,
  setValue,
  SetValueAction,
  updateGroup,
  type FormState,
} from 'ngrx-forms';
import { maxLength, minLength, pattern, required } from 'ngrx-forms/validation';

import { activateSmartChargingLeadStep } from './smart-charging-lead.actions';

export const smartChargingLeadStateSelectorKey = 'smart-charging-lead';

export const CHARGING_STATION_FORM_ID: keyof SmartChargingLeadState =
  'chargingStationForm';
export const LICENSE_PLATE_CHECK_FORM_ID: keyof SmartChargingLeadState =
  'licensePlateCheckForm';
export const licensePlateControlId = `${LICENSE_PLATE_CHECK_FORM_ID}.licensePlate`;

export enum SmartChargingLeadWidgetStep {
  ChargingStationForm,
  ChargingStationRejected,
  LicensePlateForm,
  LicensePlateAccepted,
  LicensePlateRejected,
  LicensePlateUnknown,
  ThankYouScreen,
}

export interface SmartChargingLeadState {
  readonly chargingStationForm: FormState<{
    hasChargingStation: boolean | undefined;
  }>;
  readonly licensePlateCheckForm: FormState<{ licensePlate: string }>;
  step: SmartChargingLeadWidgetStep;
}

export const initialSmartChargingLeadState: SmartChargingLeadState = {
  chargingStationForm: createFormGroupState(CHARGING_STATION_FORM_ID, {
    hasChargingStation: undefined,
  }),
  licensePlateCheckForm: createFormGroupState(LICENSE_PLATE_CHECK_FORM_ID, {
    licensePlate: '',
  }),
  step: SmartChargingLeadWidgetStep.ChargingStationForm,
};

const validateChargingStationForm = (state: SmartChargingLeadState) => {
  const chargingStationForm = updateGroup({
    hasChargingStation: validateSequential(required),
  })(state.chargingStationForm);

  let step = state.step;
  if (
    chargingStationForm.isValid &&
    chargingStationForm.value.hasChargingStation === false
  ) {
    step = SmartChargingLeadWidgetStep.ChargingStationRejected;
  } else if (
    chargingStationForm.isValid &&
    chargingStationForm.value.hasChargingStation === true
  ) {
    step = SmartChargingLeadWidgetStep.LicensePlateForm;
  }

  return {
    ...state,
    chargingStationForm,
    step,
  };
};

const licensePlateValidator = validateSequential<string>(
  required,
  minLength(6),
  maxLength(8),
  pattern(/^[A-Za-z0-9-]+$/)
);

const validateLicensePlateCheckForm = (state: SmartChargingLeadState) => ({
  ...state,
  licensePlateCheckForm: updateGroup({
    licensePlate: licensePlateValidator,
  })(markAsUnsubmitted(state.licensePlateCheckForm)),
});

const validateLicensePlateCheckFormAndSubmit = (
  state: SmartChargingLeadState
) => ({
  ...state,
  licensePlateCheckForm: updateGroup(
    {
      licensePlate: (control) => setValue(control, control.value.toUpperCase()),
    },
    {
      licensePlate: licensePlateValidator,
    }
  )(markAsSubmitted(state.licensePlateCheckForm)),
});

export const smartChargingLeadReducer = createReducer(
  initialSmartChargingLeadState,
  onNgrxForms(),
  onNgrxFormsControlId(
    MarkAsSubmittedAction,
    CHARGING_STATION_FORM_ID,
    validateChargingStationForm
  ),
  onNgrxFormsControlId(
    MarkAsSubmittedAction,
    LICENSE_PLATE_CHECK_FORM_ID,
    validateLicensePlateCheckFormAndSubmit
  ),
  onNgrxFormsControlId(
    SetValueAction,
    licensePlateControlId,
    validateLicensePlateCheckForm
  ),
  on(activateSmartChargingLeadStep, (state, { step }) => ({
    ...state,
    step,
  }))
);
